import React, { useContext, useState } from "react";
import { Container, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router";
import { MdOutlineDownload, MdOutlineCalendarToday } from "react-icons/md";
import { HiOutlineArrowNarrowLeft } from "react-icons/hi";
import { HiOutlineDocumentReport } from "react-icons/hi";
import axios from "axios";
import { usersData } from "../../App";
import ErrorModal from "../Modals/ErrorModal";
import { variables } from "../../environments";

const inputStyle = {
  width: "100%",
  padding: "11px 12px",
  border: "1.5px solid #e2e8f0",
  borderRadius: "8px",
  fontSize: "14px",
  color: "#2d3748",
  backgroundColor: "#f8fafc",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "12px",
  fontWeight: "700",
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  marginBottom: "8px",
};

const LedgerStatement = () => {
  const navigate = useNavigate();
  const { setShowMaintenance, onBehalfStatus } = useContext(usersData);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [quickSelect, setQuickSelect] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, content: "" });
  const [statementHtml, setStatementHtml] = useState("");
  const [noData, setNoData] = useState(false);
  const [contactName, setContactName] = useState("");
  const [partnerCode, setPartnerCode] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  const getRetailerCode = () => {
    if (onBehalfStatus) {
      const onBehalfCode = localStorage.getItem("onBehalfRetCode");
      if (onBehalfCode) return onBehalfCode;
    }
    let data = localStorage.getItem("userData");
    data = JSON.parse(data);
    return data?.retailerCode || "";
  };

  const toDateInputValue = (date) => date.toISOString().split("T")[0];

  const applyQuickRange = (val) => {
    const today = new Date();
    let from, to;

    if (val === "1") {
      to = new Date(today);
      from = new Date(today);
      from.setMonth(from.getMonth() - 1);
    } else if (val === "3") {
      to = new Date(today);
      from = new Date(today);
      from.setMonth(from.getMonth() - 3);
    } else if (val === "prev_fy") {
      const yr =
        today.getMonth() >= 3
          ? today.getFullYear() - 1
          : today.getFullYear() - 2;
      from = new Date(yr, 3, 1);
      to = new Date(yr + 1, 2, 31);
    } else if (val === "prev_fq") {
      const month = today.getMonth();
      const yr = today.getFullYear();
      // FY quarters: Q1=Apr-Jun, Q2=Jul-Sep, Q3=Oct-Dec, Q4=Jan-Mar
      let qStart, qEnd;
      if (month >= 3 && month <= 5) {
        qStart = new Date(yr - 1, 0, 1);
        qEnd = new Date(yr - 1, 2, 31);
      } else if (month >= 6 && month <= 8) {
        qStart = new Date(yr, 3, 1);
        qEnd = new Date(yr, 5, 30);
      } else if (month >= 9 && month <= 11) {
        qStart = new Date(yr, 6, 1);
        qEnd = new Date(yr, 8, 30);
      } else {
        qStart = new Date(yr - 1, 9, 1);
        qEnd = new Date(yr - 1, 11, 31);
      }
      from = qStart;
      to = qEnd;
    }

    if (from && to) {
      setFromDate(toDateInputValue(from));
      setToDate(toDateInputValue(to));
    }
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const inlineImagesAsBase64 = async (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const imgs = doc.querySelectorAll("img");
    await Promise.all(
      Array.from(imgs).map(async (img) => {
        const src = img.getAttribute("src");
        if (!src || src.startsWith("data:")) return;
        try {
          const res = await fetch(src);
          const blob = await res.blob();
          const b64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          img.setAttribute("src", b64);
        } catch {}
      }),
    );
    return doc.body.innerHTML;
  };

  const fetchLedgerStatement = async () => {
    if (!fromDate || !toDate) {
      setErrorModal({
        show: true,
        content: "Please select both From Date and To Date",
      });
      return;
    }
    if (fromDate > toDate) {
      setErrorModal({
        show: true,
        content: "From Date cannot be after To Date",
      });
      return;
    }

    setLoading(true);
    setStatementHtml("");
    setNoData(false);
    setContactName("");
    setPartnerCode("");

    try {
      const retailerCode = getRetailerCode();
      const payload = {
        from_date: fromDate,
        to_date: toDate,
        partner_code: retailerCode,
      };

      const res = await axios.post(variables.partnerStatementUrl, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const data = res.data;
      if (data.responseCode === "200" && data.successObject) {
        const html = data.successObject.statement_html;
        if (!html || html.trim() === "") {
          setNoData(true);
        } else {
          setContactName(data.successObject.contact_name || "");
          setPartnerCode(data.successObject.partner_code || retailerCode);
          const inlinedHtml = await inlineImagesAsBase64(html);
          setStatementHtml(inlinedHtml);
          setDateRange({ from: fromDate, to: toDate });
        }
      } else {
        setNoData(true);
      }
    } catch (error) {
      if (
        error?.response?.status === 502 ||
        error?.response?.status === 503 ||
        error?.response?.status === 504
      ) {
        setShowMaintenance({ status: true, apiData: error?.response });
      } else {
        setErrorModal({
          show: true,
          content:
            error?.response?.data?.message ||
            error.message ||
            "Something went wrong!",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!statementHtml) return;
    const printWindow = window.open("", "_blank", "width=1000,height=750");
    printWindow.document.write(statementHtml);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  return (
    <div style={{ backgroundColor: "#f0f4f8", minHeight: "100vh" }}>
      <ErrorModal
        show={errorModal.show}
        content={errorModal.content}
        onHide={() => setErrorModal({ show: false, content: "" })}
      />

      <Container fluid className="productList" style={{ height: "auto" }}>
        <Container fluid className="productListHeader font_ubuntu">
          <HiOutlineArrowNarrowLeft
            className="cursor_pointer goBack"
            onClick={() => navigate("/store")}
          />
          &nbsp;&nbsp;&nbsp; Outstanding Ledger
        </Container>
      </Container>

      <Container style={{ paddingTop: "20px", paddingBottom: "40px" }}>
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "14px",
            padding: "20px",
            boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
            marginBottom: "20px",
          }}>
          <div
            className="display_flex"
            style={{ alignItems: "center", gap: "8px", marginBottom: "18px" }}>
            <span
              style={{ fontSize: "15px", fontWeight: "700", color: "#18406E" }}>
              Select Date Range
            </span>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={labelStyle}>Quick Select</label>
            <select
              onChange={(e) => {
                const val = e.target.value;
                setQuickSelect(val);
                if (val === "custom") {
                  setFromDate("");
                  setToDate("");
                } else if (val) {
                  applyQuickRange(val);
                }
              }}
              value={quickSelect}
              style={{ ...inputStyle, cursor: "pointer" }}>
              <option value="">Select a range...</option>
              <option value="1">This Month</option>
              <option value="3">This Quarter</option>
              <option value="prev_fq">Previous Financial Quarter</option>
              <option value="prev_fy">Previous Financial Year</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {quickSelect === "custom" && (
            <div
              style={{
                display: "flex",
                gap: "12px",
                marginBottom: "18px",
                flexWrap: "wrap",
              }}>
              <div style={{ flex: 1, minWidth: "120px" }}>
                <label style={labelStyle}>
                  <MdOutlineCalendarToday style={{ fontSize: "13px" }} />
                  From Date
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1, minWidth: "120px" }}>
                <label style={labelStyle}>
                  <MdOutlineCalendarToday style={{ fontSize: "13px" }} />
                  To Date
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>
          )}

          <button
            onClick={fetchLedgerStatement}
            disabled={loading}
            style={{
              width: "100%",
              padding: "14px",
              backgroundColor: loading ? "#4a7fa5" : "#18406E",
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              fontSize: "15px",
              fontWeight: "700",
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              letterSpacing: "0.3px",
              transition: "background-color 0.2s",
            }}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" />
                Fetching Statement...
              </>
            ) : (
              <>
                <HiOutlineDocumentReport style={{ fontSize: "18px" }} />
                Get Ledger Statement
              </>
            )}
          </button>
        </div>

        {/* Statement Result */}
        {statementHtml && (
          <>
            {/* Contact + date range info bar */}
            {contactName && (
              <div
                style={{
                  backgroundColor: "#eaf3fb",
                  border: "1px solid #bcd9ef",
                  borderLeft: "4px solid #18406E",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  marginBottom: "14px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "6px",
                }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                  }}>
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#18406E",
                    }}>
                    {contactName}
                  </span>
                  {partnerCode && (
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#64748b",
                        fontWeight: "600",
                      }}>
                      Partner Code: {partnerCode}
                    </span>
                  )}
                </div>
                <span
                  style={{
                    fontSize: "12px",
                    color: "#4a7fa5",
                    fontWeight: "600",
                  }}>
                  {formatDisplayDate(dateRange.from)} –{" "}
                  {formatDisplayDate(dateRange.to)}
                </span>
              </div>
            )}

            {/* HTML Statement */}
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "14px",
                padding: "16px",
                boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                marginBottom: "16px",
                overflowX: "auto",
              }}
              dangerouslySetInnerHTML={{ __html: statementHtml }}
            />

            {/* Download Button */}
            <button
              onClick={handleDownloadPDF}
              style={{
                width: "100%",
                padding: "14px",
                backgroundColor: "#1a7f3c",
                color: "#fff",
                border: "none",
                borderRadius: "10px",
                fontSize: "15px",
                fontWeight: "700",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
              }}>
              <MdOutlineDownload style={{ fontSize: "20px" }} />
              Download / Print PDF
            </button>
          </>
        )}

        {noData && !loading && (
          <div
            style={{
              textAlign: "center",
              padding: "48px 24px",
              color: "#94a3b8",
              backgroundColor: "#fff",
              borderRadius: "14px",
              boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
            }}>
            <HiOutlineDocumentReport
              style={{ fontSize: "56px", marginBottom: "12px" }}
            />
            <p style={{ fontSize: "15px", fontWeight: "600", margin: 0 }}>
              No data available for the selected date range
            </p>
          </div>
        )}

        {!statementHtml && !noData && !loading && (
          <div
            style={{
              textAlign: "center",
              padding: "48px 24px",
              color: "#94a3b8",
            }}>
            <HiOutlineDocumentReport
              style={{ fontSize: "56px", marginBottom: "12px" }}
            />
            <p style={{ fontSize: "15px", fontWeight: "600", margin: 0 }}>
              Select a date range and tap Get Ledger Statement to view your
              statement
            </p>
          </div>
        )}
      </Container>
    </div>
  );
};

export default LedgerStatement;
